import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { BadgeCheck, Headphones, Languages, Mic2, PencilLine, ShieldCheck } from "lucide-react";
import ApplicantShell from "../components/ApplicantShell";
import { Button } from "../components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import B1SpeakingAudioRecorder from "../components/language/B1SpeakingAudioRecorder";
import config from "../config/config";
import { clearStoredAuth } from "../utils/authSession";

function getAuthHeaders() {
  const token = localStorage.getItem("token");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

async function fetchJson(path, options = {}) {
  const isFormData = typeof FormData !== "undefined" && options.body instanceof FormData;
  const response = await fetch(`${config.API_BASE_URL}${path}`, {
    ...options,
    headers: {
      ...(!isFormData && options.body ? { "Content-Type": "application/json" } : {}),
      ...getAuthHeaders(),
      ...(options.headers || {}),
    },
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(data?.message || data?.detail || `Request failed (${response.status})`);
    error.status = response.status;
    error.detail = data?.detail || "";
    throw error;
  }
  return data;
}

function extractModules(attempt) {
  return attempt?.answers?.modules || {};
}

function buildDraftState(attempt) {
  const saved = attempt?.answers?.draft_responses || {};
  return {
    reading: saved.reading || {},
    listening: saved.listening || {},
    language_elements: saved.language_elements || {},
    writing_answer: saved.writing_answer || "",
    speaking_audio_meta: saved.speaking_audio_meta || null,
    speaking_status: attempt?.speaking_status || saved.speaking_status || "not_started",
    speaking_invalid_reason: attempt?.speaking_invalid_reason || saved.speaking_invalid_reason || "",
    modules: extractModules(attempt),
  };
}

function buildAttemptPayload(draftState) {
  return {
    reading_answers: Object.entries(draftState.reading || {}).map(([item_id, selected_option]) => ({ item_id, selected_option })),
    listening_answers: Object.entries(draftState.listening || {}).map(([item_id, selected_option]) => ({ item_id, selected_option })),
    language_elements_answers: Object.entries(draftState.language_elements || {}).map(([item_id, selected_option]) => ({ item_id, selected_option })),
    writing_answer: draftState.writing_answer || "",
  };
}

function formatDate(value) {
  if (!value) return "Noch offen";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString("de-DE", { dateStyle: "medium", timeStyle: "short" });
}

function formatAttemptType(value) {
  if (value === "baseline") return "Erster Test";
  if (value === "follow_up") return "Neuer Test";
  return "Noch offen";
}

function formatReviewStatus(value) {
  if (value === "reviewed") return "Geprueft";
  if (value === "pending") return "In Pruefung";
  if (value === "not_required") return "Keine manuelle Pruefung noetig";
  return value || "Noch offen";
}

function formatScore(value) {
  return value === null || value === undefined ? "Noch offen" : `${Math.round(Number(value))}`;
}

function renderChoiceModule(items, fieldName, values, onChange) {
  return (items || []).map((item, index) => (
    <div key={item.id} className="dashboard-question-card">
      <p className="dashboard-question-card__title">{index + 1}. {item.prompt}</p>
      {item.transcript ? <p className="dashboard-question-card__copy">{item.transcript}</p> : null}
      <div className="dashboard-question-options">
        {item.options?.map((option, optionIndex) => (
          <label key={`${item.id}-${optionIndex}`} className="dashboard-option-row">
            <input type="radio" name={`${fieldName}-${item.id}`} checked={values?.[item.id] === optionIndex} onChange={() => onChange(fieldName, item.id, optionIndex)} />
            <span>{option}</span>
          </label>
        ))}
      </div>
    </div>
  ));
}

export default function ApplicantB1Workspace() {
  const navigate = useNavigate();
  const [attempts, setAttempts] = useState([]);
  const [latestAttempt, setLatestAttempt] = useState(null);
  const [activeAttempt, setActiveAttempt] = useState(null);
  const [draftState, setDraftState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [actionBusy, setActionBusy] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [draftDirty, setDraftDirty] = useState(false);
  const [sessionExpiredOpen, setSessionExpiredOpen] = useState(false);
  const [sessionExpiredMessage, setSessionExpiredMessage] = useState("Deine Sitzung ist abgelaufen. Bitte melde dich erneut an.");

  useEffect(() => {
    document.title = "Experts4Europe | Sprachtest B1";
  }, []);

  const handleAuthError = (err) => {
    if (Number(err?.status) !== 401) return false;
    clearStoredAuth();
    setDialogOpen(false);
    setActionBusy(false);
    const detail = String(err?.detail || err?.message || "").toLowerCase();
    const isTimeout = detail.includes("expired") || detail.includes("inactivity") || detail.includes("session");
    setSessionExpiredMessage(
      isTimeout
        ? "Du wurdest wegen Inaktivitaet automatisch abgemeldet. Bitte melde dich erneut an."
        : "Deine Anmeldung ist nicht mehr gueltig. Bitte melde dich erneut an."
    );
    setSessionExpiredOpen(true);
    return true;
  };

  const loadLanguageTestState = async () => {
    setLoading(true);
    setError("");
    try {
      const [attemptsData, latestData] = await Promise.all([
        fetchJson("/api/language-tests/b1/me/attempts"),
        fetchJson("/api/language-tests/b1/me/latest"),
      ]);
      const rows = Array.isArray(attemptsData?.rows) ? attemptsData.rows : [];
      const draftAttempt = latestData?.draft_attempt || rows.find((row) => row.status === "draft") || null;
      setAttempts(rows);
      setLatestAttempt(latestData?.attempt || rows.find((row) => row.status !== "draft") || null);
      setActiveAttempt(draftAttempt);
      setDraftState(draftAttempt ? buildDraftState(draftAttempt) : null);
      setDraftDirty(false);
    } catch (err) {
      if (handleAuthError(err)) return;
      setError(err.message || "Der Sprachtest konnte nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadLanguageTestState();
  }, []);

  async function startAttempt(testType) {
    setActionBusy(true);
    setError("");
    setNotice("");
    try {
      const result = await fetchJson("/api/language-tests/b1/attempts", { method: "POST", body: JSON.stringify({ test_type: testType }) });
      setActiveAttempt(result.attempt);
      setDraftState(buildDraftState(result.attempt));
      setDraftDirty(false);
      setDialogOpen(true);
      setNotice(testType === "baseline" ? "Erster Test gestartet." : "Neuer Test gestartet.");
      await loadLanguageTestState();
    } catch (err) {
      if (handleAuthError(err)) return;
      setError(err.message || "Der Test konnte nicht gestartet werden.");
    } finally {
      setActionBusy(false);
    }
  }

  async function saveDraft() {
    if (!activeAttempt || !draftState) return;
    setActionBusy(true);
    setError("");
    setNotice("");
    try {
      const result = await fetchJson(`/api/language-tests/b1/attempts/${activeAttempt.id}/draft`, { method: "PATCH", body: JSON.stringify(buildAttemptPayload(draftState)) });
      setActiveAttempt(result.attempt);
      setDraftState(buildDraftState(result.attempt));
      setDraftDirty(false);
      setNotice("Entwurf gespeichert.");
      await loadLanguageTestState();
    } catch (err) {
      if (handleAuthError(err)) return;
      setError(err.message || "Der Entwurf konnte nicht gespeichert werden.");
    } finally {
      setActionBusy(false);
    }
  }

  async function submitAttempt() {
    if (!activeAttempt || !draftState) return;
    setActionBusy(true);
    setError("");
    setNotice("");
    try {
      await fetchJson(`/api/language-tests/b1/attempts/${activeAttempt.id}/submit`, { method: "POST", body: JSON.stringify(buildAttemptPayload(draftState)) });
      setDialogOpen(false);
      setActiveAttempt(null);
      setDraftState(null);
      setDraftDirty(false);
      setNotice("Der B1-Core wurde eingereicht.");
      await loadLanguageTestState();
    } catch (err) {
      if (handleAuthError(err)) return;
      setError(err.message || "Der Test konnte nicht eingereicht werden.");
    } finally {
      setActionBusy(false);
    }
  }

  async function uploadSpeakingAudio(file, durationMs) {
    if (!activeAttempt?.id) return;
    setActionBusy(true);
    setError("");
    setNotice("");
    try {
      const formData = new FormData();
      formData.append("audio", file);
      formData.append("duration_ms", String(durationMs || 0));
      const result = await fetchJson(`/api/language-tests/b1/attempts/${activeAttempt.id}/speaking-audio`, { method: "POST", body: formData });
      setActiveAttempt(result.attempt);
      setDraftState(buildDraftState(result.attempt));
      setDraftDirty(false);
      setNotice("Sprachaufnahme gespeichert.");
      await loadLanguageTestState();
    } catch (err) {
      if (handleAuthError(err)) return;
      setError(err.message || "Die Sprachaufnahme konnte nicht gespeichert werden.");
      throw err;
    } finally {
      setActionBusy(false);
    }
  }

  async function setEmployerRelease(released) {
    if (!latestAttempt?.id) return;
    setActionBusy(true);
    setError("");
    setNotice("");
    try {
      await fetchJson(`/api/language-tests/b1/attempts/${latestAttempt.id}/employer-signal`, { method: "POST", body: JSON.stringify({ released }) });
      setNotice(released ? "Dein Sprachsignal ist jetzt fuer Firmen sichtbar." : "Dein Sprachsignal bleibt intern.");
      await loadLanguageTestState();
    } catch (err) {
      if (handleAuthError(err)) return;
      setError(err.message || "Die Freigabe konnte nicht geaendert werden.");
    } finally {
      setActionBusy(false);
    }
  }

  const hasCompletedAttempt = attempts.some((attempt) => attempt.status === "completed");
  const currentModules = draftState?.modules || {};
  const scoreCards = latestAttempt ? [
    { label: "Lesen", value: latestAttempt.reading_score, icon: BadgeCheck },
    { label: "Hoeren", value: latestAttempt.listening_score, icon: Headphones },
    { label: "Bausteine", value: latestAttempt.language_elements_score, icon: Languages },
    { label: "Schreiben", value: latestAttempt.writing_score, icon: PencilLine },
    { label: "Sprechen", value: latestAttempt.speaking_score, icon: Mic2 },
  ] : [];
  const searchItems = useMemo(() => ([
    { href: "/dashboard/applicant", label: "Dashboard", description: "Zurueck zur Mission Control" },
    { href: "/dashboard/applicant/sprachtest-b1", label: "Sprachtest B1", description: "Teststatus, Verlauf und Freigabe" },
    { href: "/dashboard/applicant/bewerbung", label: "Bewerbung", description: "CV und Skills weiter pflegen" },
  ]), []);

  return (
    <ApplicantShell
      title="Sprachtest B1"
      subtitle="Hier liegt nur dein Deutsch-Test: Status, Versuch, Verlauf und Freigabe."
      actions={<><Button variant="outline" className="applicant-cta-button applicant-cta-button-secondary" onClick={() => navigate("/dashboard/applicant")}>Zur Mission Control</Button><Button className="applicant-cta-button" onClick={() => activeAttempt ? setDialogOpen(true) : startAttempt(hasCompletedAttempt ? "follow_up" : "baseline")} disabled={loading || actionBusy}>{activeAttempt ? "Test fortsetzen" : hasCompletedAttempt ? "Neuen Test starten" : "Test starten"}</Button></>}
      notifications={[]}
      searchItems={searchItems}
    >
      <div className="applicant-workspace-page">
        {error ? <div className="dashboard-inline-alert dashboard-inline-alert-danger">{error}</div> : null}
        {notice ? <div className="dashboard-inline-alert dashboard-inline-alert-success">{notice}</div> : null}
        <section className="applicant-workspace-hero applicant-workspace-hero--b1">
          <div className="applicant-workspace-hero__main">
            <span className="applicant-section-kicker">Deutsch B1</span>
            <h2>Dein Test. Dein Ergebnis. Dein naechster Versuch.</h2>
            <p>Du siehst hier nur den Sprachtest selbst: Status, Ergebnis, Verlauf und Freigabe fuer Firmen.</p>
            <div className="applicant-workspace-chip-row">
              <span className="applicant-status-pill"><Languages size={15} />{latestAttempt?.estimated_cefr_band || (activeAttempt ? "Entwurf offen" : "Noch offen")}</span>
              <span className="applicant-status-pill"><BadgeCheck size={15} />{latestAttempt ? formatReviewStatus(latestAttempt.review_status) : "Noch nicht geprueft"}</span>
              <span className="applicant-status-pill"><ShieldCheck size={15} />{latestAttempt?.employer_signal_released ? "Fuer Firmen sichtbar" : "Nur intern"}</span>
            </div>
          </div>
          <aside className="applicant-readiness-panel applicant-readiness-panel--language">
            <div className="applicant-readiness-orb applicant-readiness-orb--language">
              <strong>{latestAttempt?.overall_language_score ? Math.round(Number(latestAttempt.overall_language_score)) : "--"}</strong>
              <span>Deutsch-Score</span>
              <small>{latestAttempt?.estimated_cefr_band || "wird geladen"}</small>
            </div>
          </aside>
        </section>

        <section className="applicant-workspace-scoreboard">
          {scoreCards.length ? scoreCards.map((item) => (<article key={item.label} className="applicant-signal-tile"><span>{item.label}</span><strong>{formatScore(item.value)}</strong><small>Modulwert</small></article>)) : <div className="applicant-empty-card">Nach dem ersten abgeschlossenen Test siehst du hier deine Modulwerte.</div>}
        </section>

        <div className="applicant-workspace-grid applicant-workspace-grid--main">
          <section className="applicant-workspace-card">
            <div className="applicant-workspace-card__head"><div><span className="applicant-section-kicker">Jetzt</span><h3>Dein Teststatus</h3></div></div>
            <div className="applicant-checklist">
              <div className="applicant-checklist__item is-done"><BadgeCheck size={16} /><span>{latestAttempt ? `Letztes Ergebnis: ${latestAttempt.estimated_cefr_band || "Noch offen"}` : "Noch kein abgeschlossener Test"}</span></div>
              <div className="applicant-checklist__item is-open"><Languages size={16} /><span>{activeAttempt ? "Ein Entwurf wartet auf dich." : hasCompletedAttempt ? "Du kannst jetzt einen neuen Test starten." : "Starte deinen ersten B1-Test."}</span></div>
            </div>
          </section>
          <section className="applicant-workspace-card">
            <div className="applicant-workspace-card__head"><div><span className="applicant-section-kicker">Freigabe</span><h3>Fuer Arbeitgeber sichtbar</h3></div></div>
            <p className="applicant-workspace-card__copy">Nur CEFR-Band, Gesamtwert und Trend werden geteilt. Audio, Schreiben und Details bleiben intern.</p>
            <div className="applicant-workspace-actions"><Button variant="outline" className="applicant-cta-button applicant-cta-button-secondary" onClick={() => setEmployerRelease(false)} disabled={!latestAttempt || actionBusy}>Intern halten</Button><Button className="applicant-cta-button" onClick={() => setEmployerRelease(true)} disabled={!latestAttempt || actionBusy}>Freigeben</Button></div>
          </section>
        </div>

        <section className="applicant-workspace-card">
          <Tabs defaultValue="overview">
            <TabsList className="applicant-tablist applicant-tablist-secondary">
              <TabsTrigger value="overview" className="applicant-tabtrigger">Uebersicht</TabsTrigger>
              <TabsTrigger value="history" className="applicant-tabtrigger">Verlauf</TabsTrigger>
              <TabsTrigger value="release" className="applicant-tabtrigger">Freigabe</TabsTrigger>
            </TabsList>
            <TabsContent value="overview" className="applicant-tabcontent"><div className="applicant-overview-grid"><article className="applicant-detail-card"><span className="applicant-section-kicker">Aktuell</span><strong>{latestAttempt?.estimated_cefr_band || (activeAttempt ? "Entwurf offen" : "Noch offen")}</strong><p>{latestAttempt ? `${formatScore(latestAttempt.overall_language_score)} Punkte · ${formatAttemptType(latestAttempt.test_type)}` : "Mit deinem ersten Test bekommst du dein klares Sprachsignal."}</p></article><article className="applicant-detail-card"><span className="applicant-section-kicker">Naechster Schritt</span><strong>{activeAttempt ? "Entwurf fortsetzen" : hasCompletedAttempt ? "Neuen Test planen" : "Ersten Test starten"}</strong><p>{activeAttempt ? "Dein B1-Core wartet auf dich." : "Der naechste Versuch bringt dir ein neues Sprachsignal."}</p></article></div></TabsContent>
            <TabsContent value="history" className="applicant-tabcontent"><div className="applicant-history-stack">{attempts.length ? attempts.map((attempt) => <article key={attempt.id} className="applicant-history-card"><div><strong>Sprachtest #{attempt.id}</strong><p>{formatAttemptType(attempt.test_type)} · {formatReviewStatus(attempt.review_status)}</p></div><div className="applicant-history-card__meta"><span>{attempt.estimated_cefr_band || "Noch offen"}</span><small>{formatDate(attempt.completed_at || attempt.created_at)}</small></div></article>) : <div className="applicant-empty-card">Noch keine Versuchshistorie vorhanden.</div>}</div></TabsContent>
            <TabsContent value="release" className="applicant-tabcontent"><div className="applicant-overview-grid"><article className="applicant-detail-card"><span className="applicant-section-kicker">Freigabe</span><strong>{latestAttempt?.employer_signal_released ? "Signal geteilt" : "Signal intern"}</strong><p>{latestAttempt ? `Review: ${formatReviewStatus(latestAttempt.review_status)}` : "Freigabe wird nach dem ersten B1-Core relevant."}</p></article><article className="applicant-detail-card"><span className="applicant-section-kicker">Was Firmen sehen</span><strong>Kompaktes Sprachsignal</strong><p>CEFR-Band, Gesamtwert und Trend. Audio, Text und Detailbewertung bleiben intern.</p></article></div></TabsContent>
          </Tabs>
        </section>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent fullScreen className="dashboard-dialog">
          <DialogHeader className="dashboard-dialog__header"><DialogTitle className="dashboard-dialog__title">Sprachtest B1 Core</DialogTitle><DialogDescription className="dashboard-dialog__description">Hier bearbeitest du nur die Testmodule selbst.</DialogDescription></DialogHeader>
          <div className="dashboard-dialog__content">
            <div className="dashboard-dialog__main">
              <section className="dashboard-dialog__section"><h3>Lesen</h3>{renderChoiceModule(currentModules.reading?.items, "reading", draftState?.reading, (moduleKey, itemId, value) => { setDraftState((current) => ({ ...current, [moduleKey]: { ...(current?.[moduleKey] || {}), [itemId]: Number(value) } })); setDraftDirty(true); })}</section>
              <section className="dashboard-dialog__section"><h3>Hoeren</h3>{renderChoiceModule(currentModules.listening?.items, "listening", draftState?.listening, (moduleKey, itemId, value) => { setDraftState((current) => ({ ...current, [moduleKey]: { ...(current?.[moduleKey] || {}), [itemId]: Number(value) } })); setDraftDirty(true); })}</section>
              <section className="dashboard-dialog__section"><h3>Sprachbausteine</h3>{renderChoiceModule(currentModules.language_elements?.items, "language_elements", draftState?.language_elements, (moduleKey, itemId, value) => { setDraftState((current) => ({ ...current, [moduleKey]: { ...(current?.[moduleKey] || {}), [itemId]: Number(value) } })); setDraftDirty(true); })}</section>
              <section className="dashboard-dialog__section"><h3>Schreiben</h3><div className="dashboard-question-card"><p className="dashboard-question-card__copy">{currentModules.writing?.instructions}</p><textarea className="dashboard-textarea" placeholder="Schreibe deine Antwort hier." value={draftState?.writing_answer || ""} onChange={(event) => { setDraftState((current) => ({ ...current, writing_answer: event.target.value })); setDraftDirty(true); }} /></div></section>
              <section className="dashboard-dialog__section"><h3>Sprechen</h3><div className="dashboard-question-card"><p className="dashboard-question-card__meta">Audioaufgabe</p><ul className="dashboard-bullet-list">{(currentModules.speaking?.prompts || []).map((prompt) => <li key={prompt}>{prompt}</li>)}</ul></div><B1SpeakingAudioRecorder attemptId={activeAttempt?.id} currentAudio={activeAttempt?.speaking_audio || draftState?.speaking_audio_meta} speakingStatus={activeAttempt?.speaking_status || draftState?.speaking_status} invalidReason={activeAttempt?.speaking_invalid_reason || draftState?.speaking_invalid_reason} prompts={currentModules.speaking?.prompts || []} disabled={actionBusy || !activeAttempt} onUpload={uploadSpeakingAudio} /></section>
            </div>
            <aside className="dashboard-dialog__side">
              <section className="dashboard-dialog__section"><h3>Versuchssteuerung</h3><div className="dashboard-surface-grid"><div className="dashboard-surface"><span className="dashboard-summary-label">Versuch</span><strong>{formatAttemptType(activeAttempt?.test_type)}</strong></div><div className="dashboard-surface"><span className="dashboard-summary-label">Core-Version</span><strong>{activeAttempt?.core_version || "b1_core_v1"}</strong></div><div className="dashboard-surface"><span className="dashboard-summary-label">Stand</span><strong>{draftDirty ? "Ungespeichert" : "Synchron"}</strong></div></div><div className="dashboard-dialog__actions"><Button variant="outline" className="dashboard-action-button dashboard-action-button-secondary" onClick={saveDraft} disabled={actionBusy || !activeAttempt}>Entwurf speichern</Button><Button className="dashboard-action-button" onClick={submitAttempt} disabled={actionBusy || !activeAttempt}>B1-Core absenden</Button></div></section>
              <section className="dashboard-dialog__section"><h3>Letzte Versuche</h3><Accordion type="single" collapsible className="dashboard-accordion"><AccordionItem value="attempts" className="dashboard-accordion__item"><AccordionTrigger className="dashboard-accordion__trigger">Historie</AccordionTrigger><AccordionContent className="dashboard-accordion__content"><div className="dashboard-history-list">{attempts.length ? attempts.slice(0, 6).map((attempt) => <div key={attempt.id} className="dashboard-history-item"><div><strong>Versuch #{attempt.id}</strong><p>{formatAttemptType(attempt.test_type)} · {formatReviewStatus(attempt.review_status)}</p></div><div className="dashboard-history-meta"><span>{attempt.estimated_cefr_band || "Noch offen"}</span><small>{formatDate(attempt.completed_at || attempt.created_at)}</small></div></div>) : <div className="dashboard-empty-tile">Noch keine gespeicherten Versuche vorhanden.</div>}</div></AccordionContent></AccordionItem></Accordion></section>
            </aside>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={sessionExpiredOpen} onOpenChange={() => {}}>
        <DialogContent className="max-w-[460px]">
          <DialogHeader>
            <DialogTitle>Sitzung abgelaufen</DialogTitle>
            <DialogDescription>{sessionExpiredMessage}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-wrap justify-end gap-3">
            <Button variant="outline" className="applicant-cta-button applicant-cta-button-secondary" onClick={() => navigate("/")}>Startseite</Button>
            <Button className="applicant-cta-button" onClick={() => navigate("/login", { replace: true })}>Erneut anmelden</Button>
          </div>
        </DialogContent>
      </Dialog>
    </ApplicantShell>
  );
}
